import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";import { useAuth } from "@/context/AuthContext";
import { useSipPhone } from "@/components/sales/SipPhoneProvider";
import { useTheme } from "next-themes";
import {
  LayoutDashboard, Building2, Users, Layout, ChevronLeft, ChevronRight,
  Sun, Moon, LogOut, Menu, ShieldCheck, Settings as SettingsIcon,
  Phone, PhoneOff, Loader2, CalendarDays, Globe
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const navItems = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/firmen", icon: Building2, label: "Firmen" },
  { to: "/leads", icon: Users, label: "Leads" },
  { to: "/termine", icon: CalendarDays, label: "Termine" },
  { to: "/templates", icon: Layout, label: "Templates" },
  { to: "/live-sites", icon: Globe, label: "Live Sites", adminOnly: true },
  { to: "/team", icon: ShieldCheck, label: "Team", adminOnly: true },
];

export default function AppLayout({ children }) {
  const [collapsed, setCollapsed] = useState(false);
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const sip = useSipPhone();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <TooltipProvider delayDuration={0}>
      <div className="flex h-screen bg-background text-foreground overflow-hidden">
        {/* Sidebar */}
        <aside
          data-testid="sidebar"
          className={`flex flex-col border-r border-border bg-card transition-all duration-200 ease-in-out ${collapsed ? "w-16" : "w-56"} shrink-0`}
        >
          {/* Logo */}
          <div className={`flex items-center border-b border-border h-14 px-4 ${collapsed ? "justify-center" : "justify-between"}`}>
            {!collapsed && (
              <span className="font-heading font-bold text-sm tracking-tight text-foreground flex items-center gap-1.5">
                <div className="w-5 h-5 rounded bg-cyan-400 flex items-center justify-center flex-shrink-0">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" fill="#0f172a" stroke="#0f172a" strokeWidth="1" strokeLinejoin="round"/>
                  </svg>
                </div>
                signal<span className="text-cyan-400">Solution</span>
              </span>
            )}
            <button
              data-testid="sidebar-toggle"
              onClick={() => setCollapsed(!collapsed)}
              className="p-1 rounded-sm hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            >
              {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
            </button>
          </div>

          {/* Nav */}
          <nav className="flex-1 py-3 space-y-0.5 px-2">
            {navItems.filter(item => !item.adminOnly || user?.role === "admin").map(({ to, icon: Icon, label }) => (
              <Tooltip key={to}>
                <TooltipTrigger asChild>
                  <NavLink
                    to={to}
                    data-testid={`nav-${label.toLowerCase()}`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-2 py-2 rounded-sm text-sm font-medium transition-colors ${
                        isActive
                          ? "bg-muted text-foreground"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      } ${collapsed ? "justify-center" : ""}`
                    }
                  >
                    <Icon size={16} className="shrink-0" />
                    {!collapsed && <span>{label}</span>}
                  </NavLink>
                </TooltipTrigger>
                {collapsed && <TooltipContent side="right">{label}</TooltipContent>}
              </Tooltip>
            ))}
          </nav>

          {/* Bottom */}
          <div className="border-t border-border p-2 space-y-1">
            {user?.role === "admin" && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <NavLink
                    to="/einstellungen"
                    data-testid="nav-einstellungen"
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-2 py-2 rounded-sm text-sm transition-colors w-full ${
                        isActive
                          ? "bg-muted text-foreground"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      } ${collapsed ? "justify-center" : ""}`
                    }
                  >
                    <SettingsIcon size={16} />
                    {!collapsed && <span>Einstellungen</span>}
                  </NavLink>
                </TooltipTrigger>
                {collapsed && <TooltipContent side="right">Einstellungen</TooltipContent>}
              </Tooltip>
            )}

            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  data-testid="theme-toggle"
                  onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
                  className={`flex items-center gap-3 px-2 py-2 rounded-sm text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors w-full ${collapsed ? "justify-center" : ""}`}
                >
                  {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
                  {!collapsed && <span>{theme === "dark" ? "Hell" : "Dunkel"}</span>}
                </button>
              </TooltipTrigger>
              {collapsed && <TooltipContent side="right">Design wechseln</TooltipContent>}
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  data-testid="logout-btn"
                  onClick={handleLogout}
                  className={`flex items-center gap-3 px-2 py-2 rounded-sm text-sm text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors w-full ${collapsed ? "justify-center" : ""}`}
                >
                  <LogOut size={16} />
                  {!collapsed && <span>Abmelden</span>}
                </button>
              </TooltipTrigger>
              {collapsed && <TooltipContent side="right">Abmelden</TooltipContent>}
            </Tooltip>

            {!collapsed && user && (
              <div className="px-2 pt-2 pb-1">
                <p className="text-xs font-medium text-foreground truncate">{user.name || user.email}</p>
                <p className="text-xs text-muted-foreground truncate">{user.role}</p>
              </div>
            )}
          </div>
        </aside>

        {/* Main */}
        <main className="flex-1 flex flex-col overflow-hidden">
          {/* Header */}
          <header className="h-14 border-b border-border bg-card flex items-center px-6 shrink-0">
            <div className="flex-1" />
            {user && (
              <div className="flex items-center gap-3">
                {/* SIP Phone status pill */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => !sip.registered && !sip.registering && sip.connect()}
                      data-testid="sip-status-pill"
                      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
                        sip.registered
                          ? "bg-green-500/10 text-green-600 border-green-500/30 dark:text-green-400"
                          : sip.registering
                          ? "bg-amber-500/10 text-amber-600 border-amber-500/30 dark:text-amber-400 cursor-wait"
                          : "bg-muted text-muted-foreground border-border hover:border-primary/40 cursor-pointer"
                      }`}
                    >
                      {sip.registered ? <Phone size={11}/> : sip.registering ? <Loader2 size={11} className="animate-spin"/> : <PhoneOff size={11}/>}
                      <span className="hidden sm:inline">
                        {sip.registered ? "Telefon bereit" : sip.registering ? "Verbinde…" : "Telefon offline"}
                      </span>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    {sip.registered ? `Browser-Telefon aktiv${sip.credentials?.alias ? ` (${sip.credentials.alias})` : ""}`
                      : sip.registering ? "Verbinde mit Sipgate…"
                      : "Klick zum Verbinden"}
                  </TooltipContent>
                </Tooltip>
                <span className="text-xs text-muted-foreground font-mono">{user.email}</span>
                <div className="w-7 h-7 rounded-sm bg-primary flex items-center justify-center text-primary-foreground text-xs font-bold">
                  {(user.name || user.email)[0].toUpperCase()}
                </div>
              </div>
            )}
          </header>

          {/* Content */}
          <div className="flex-1 overflow-auto p-6">
            {children}
          </div>
        </main>
      </div>
    </TooltipProvider>
  );
}
